// src/pages/BoardCreate.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';

export default function BoardCreate({ user }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePost = async e => {
    e.preventDefault();
    if (!title.trim())    return alert('제목을 입력하세요.');
    if (!content.trim())  return alert('내용을 입력하세요.');

    try {
      setIsSubmitting(true);
      await addDoc(collection(db, 'posts'), {
        title: title.trim(),
        content: content.trim(),
        authorId: user.id,
        authorName: isAnonymous ? '익명' : user.nickname,
        authorIconIdx: user.iconIdx ?? 0,
        isAnonymous,
        createdAt: serverTimestamp()
      });
      navigate('/board');
    } catch (error) {
      console.error('게시글 작성 중 오류:', error);
      alert('게시글 작성 중 오류가 발생했습니다.');
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ 
      padding: isMobile ? '1rem' : '2rem',
      maxWidth: '100%',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
      }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? '1.5rem' : '2rem',
          fontWeight: 700,
          color: '#1e293b',
        }}>새 게시물 작성</h2>
        <button 
          onClick={() => navigate('/board')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.6rem 1rem',
            background: 'transparent',
            color: '#64748b',
            border: '1px solid #e2e8f0',
            borderRadius: '0.5rem',
            fontWeight: '500',
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          목록으로
        </button>
      </div>
      
      {/* 폼 영역 */}
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        border: '1px solid #f1f5f9',
        marginBottom: isMobile ? '5rem' : '2rem',
      }}>
        <form onSubmit={handlePost}>
          {/* 제목 입력 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>제목</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
            />
          </div>

          {/* 내용 입력 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>내용</label>
            <textarea
              rows={isMobile ? 10 : 15}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="내용을 입력하세요"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                outline: 'none',
                resize: 'vertical',
                minHeight: '150px',
                lineHeight: '1.5',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* 익명 체크박스 */}
          <div style={{ 
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: '0.95rem',
              color: '#475569',
            }}>
              <div style={{
                width: '1.25rem',
                height: '1.25rem',
                border: '1.5px solid #cbd5e1',
                borderRadius: '0.25rem',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isAnonymous ? '#6366f1' : 'white',
              }}>
                {isAnonymous && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={e => setIsAnonymous(e.target.checked)}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>익명으로 작성</span>
                {isAnonymous && (
                  <div style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    borderRadius: '1rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                  }}>
                    🕵️ 익명
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* 버튼 영역 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={() => navigate('/board')}
              style={{
                padding: isMobile ? '0.8rem' : '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #cbd5e1',
                background: 'transparent',
                color: '#64748b',
                fontWeight: '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
                flex: isMobile ? '1 0 100%' : 'initial',
                order: isMobile ? 2 : 1,
                marginBottom: isMobile ? '0' : '0',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: isMobile ? '0.8rem' : '0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: '#6366f1',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                flex: isMobile ? '1 0 100%' : 1,
                order: isMobile ? 1 : 2,
                marginBottom: isMobile ? '0.5rem' : '0',
                opacity: isSubmitting ? 0.7 : 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10" strokeOpacity="0.75" />
                  </svg>
                  게시 중...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  등록하기
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
